'use client'

import { Cell, InfoBanner, InputText, Label, Spacer } from '@vinted/web-ui'
import { omit } from 'lodash'
import { useEffect } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'

import {
  PersonalIdNumberInput,
  validatePersonalIdNumber,
} from '@marketplace-web/pay-identity/pay-identity-kyc-feature'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { InputBirthdate } from '@marketplace-web/common-components/input-birthdate-ui'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import {
  renderValidation,
  useFormValidationMessage,
} from '@marketplace-web/vendor-abstractions/react-hook-form-feature'
import { NationalitySelect } from '@marketplace-web/nationality-select/nationality-select-feature'
import { NationalityCountryModel } from '@marketplace-web/nationality-select/nationality-select-data'
import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'

import { Field, FormModel } from '../../types'
import { getIsPersonalIdNumberFieldRequired } from '../../utils'
import { getIsFieldLocked, getIsFieldMasked } from '../utils'
import { validateTooYoung } from '../validators/birthdate'

type Props = {
  isLoading: boolean
  wide?: boolean
  showTitle?: boolean
  bannedNationalities: Array<string>
  showInformationHint?: boolean
  isSsnNumberRequired?: boolean
  isNationalityRequired?: boolean
  isBalanceActivated?: boolean
  personalIdNumberRequiredNationalities?: Array<string> | null
  lockedFields: Array<string>
  maskedFields?: Array<string>
}

const GeneralDetails = ({
  isLoading,
  wide,
  showTitle,
  bannedNationalities,
  showInformationHint,
  isSsnNumberRequired,
  isNationalityRequired,
  isBalanceActivated,
  personalIdNumberRequiredNationalities,
  lockedFields,
  maskedFields,
}: Props) => {
  const translate = useTranslate('wallet.activation.general')
  const inputBirthdateTranslate = useTranslate()
  const intl = useIntl()
  const breakpoints = useBreakpoint()

  const {
    register,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<FormModel>()

  const validationMessage = useFormValidationMessage(errors, 'wallet.activation.general.fields')

  const selectedNationality = watch(Field.Nationality)
  const isPersonalIdNumberFieldRequired = getIsPersonalIdNumberFieldRequired(
    personalIdNumberRequiredNationalities,
    selectedNationality,
  )

  const validateNationality = (country: NationalityCountryModel | null) => {
    if (!isNationalityRequired || !bannedNationalities.length) return true

    if (!country) return false

    return !bannedNationalities.includes(country.code)
  }

  useEffect(() => {
    if (!isPersonalIdNumberFieldRequired) return

    setValue(Field.PersonalIdNumber, '')
  }, [isPersonalIdNumberFieldRequired, setValue])

  useEffect(() => {
    if (!isNationalityRequired) return

    register(Field.Nationality, { required: true })
  }, [register, isNationalityRequired])

  const inputStyling = wide ? InputText.Styling.Wide : InputText.Styling.Narrow

  function handleNationalityChange(onChange: (...event: Array<unknown>) => void) {
    return (country: NationalityCountryModel | null) => {
      onChange(country)
    }
  }

  function getIsFieldDisabled(field: Field) {
    return isLoading || getIsFieldLocked(field, lockedFields)
  }

  function renderNames() {
    return (
      <>
        <InputText
          {...register(Field.FirstName, {
            required: true,
          })}
          styling={inputStyling}
          disabled={getIsFieldDisabled(Field.FirstName)}
          maxLength={50}
          title={translate('fields.first_name.title')}
          placeholder={translate('fields.first_name.placeholder')}
          validation={renderValidation(validationMessage(Field.FirstName))}
        />
        <InputText
          {...register(Field.LastName, {
            required: true,
          })}
          styling={inputStyling}
          disabled={getIsFieldDisabled(Field.LastName)}
          maxLength={50}
          title={translate('fields.last_name.title')}
          placeholder={translate('fields.last_name.placeholder')}
          validation={renderValidation(validationMessage(Field.LastName))}
        />
      </>
    )
  }

  function renderBirthdate() {
    if (isPersonalIdNumberFieldRequired) return null

    return (
      <Controller
        shouldUnregister
        render={({ field }) => {
          const fieldPropsWithoutRef = omit(field, 'ref')

          return (
            <InputBirthdate
              {...fieldPropsWithoutRef}
              isPhone={!!breakpoints.phones}
              styling={inputStyling}
              title={translate('fields.birthdate.title')}
              dayPlaceholder={inputBirthdateTranslate('birthdate.day')}
              monthPlaceholder={inputBirthdateTranslate('birthdate.month')}
              yearPlaceholder={inputBirthdateTranslate('birthdate.year')}
              isoLocale={intl.locale}
              disabled={getIsFieldDisabled(Field.Birthdate)}
              validation={renderValidation(validationMessage(Field.Birthdate))}
            />
          )
        }}
        name={Field.Birthdate}
        control={control}
        rules={{
          required: true,
          validate: validateTooYoung,
        }}
      />
    )
  }

  function renderSsnField() {
    if (!isSsnNumberRequired) return null

    const isSnnDisabled = getIsFieldDisabled(Field.SsnNumber)

    return (
      <InputText
        {...register(Field.SsnNumber, {
          required: !isSnnDisabled,
          onChange: event => setValue(Field.SsnNumber, event.target.value),
        })}
        styling={inputStyling}
        title={translate('fields.ssn.title')}
        placeholder={isSnnDisabled ? '*'.repeat(4) : translate('fields.ssn.placeholder')}
        maxLength={4}
        disabled={isSnnDisabled}
        validation={
          renderValidation(validationMessage(Field.SsnNumber)) || translate('fields.ssn.note')
        }
        value={isSnnDisabled ? '*'.repeat(4) : undefined}
      />
    )
  }

  function renderPersonalIdNumberField() {
    if (!isPersonalIdNumberFieldRequired) return null

    const rules = getIsFieldMasked(Field.PersonalIdNumber, maskedFields)
      ? {}
      : {
          required: true,
          validate: {
            isValid: value =>
              validatePersonalIdNumber(selectedNationality?.code || '')(value).isValid ||
              translate('fields.personal_id_number.errors.validate'),
            isValidAge: value =>
              validatePersonalIdNumber(selectedNationality?.code || '')(value).isValidAge ||
              translate('fields.personal_id_number.errors.age_restricted'),
          },
        }

    return (
      <Controller
        shouldUnregister
        control={control}
        name={Field.PersonalIdNumber}
        rules={rules}
        render={({ field }) => {
          const fieldPropsWithoutRef = omit(field, 'ref')

          return (
            <PersonalIdNumberInput
              {...fieldPropsWithoutRef}
              isMasked={getIsFieldMasked(Field.PersonalIdNumber, maskedFields)}
              isDisabled={getIsFieldDisabled(Field.PersonalIdNumber)}
              isWide={wide}
              errors={errors}
            />
          )
        }}
      />
    )
  }

  function renderNationality() {
    if (!isNationalityRequired) return null

    return (
      <Controller
        control={control}
        name={Field.Nationality}
        rules={{
          required: true,
          validate: {
            isNationalityBanned: validateNationality,
          },
        }}
        render={({ field: { onChange } }) => (
          <NationalitySelect
            name={Field.Nationality}
            styling={inputStyling}
            value={selectedNationality}
            disabled={getIsFieldDisabled(Field.Nationality)}
            showSuggestedCountries={!isBalanceActivated}
            title={translate('fields.nationality.title')}
            placeholder={translate('fields.nationality.placeholder')}
            validation={renderValidation(validationMessage(Field.Nationality))}
            onChange={handleNationalityChange(onChange)}
          />
        )}
      />
    )
  }

  const renderTitle = () => {
    if (!showTitle) return null

    return (
      <Label styling={Label.Styling.Wide} type={Label.Type.Leading} text={translate('title')} />
    )
  }

  const renderInfoBanner = () => {
    if (!showInformationHint) return null

    return <InfoBanner body={translate('info_banner.body')} />
  }

  return (
    <>
      {renderTitle()}
      <Cell styling={wide ? Cell.Styling.Tight : undefined}>
        {renderInfoBanner()}
        <SeparatedList separator={<Spacer />}>
          {renderNames()}
          {renderNationality()}
          {renderBirthdate()}
          {renderSsnField()}
          {renderPersonalIdNumberField()}
        </SeparatedList>
      </Cell>
    </>
  )
}

export default GeneralDetails
